// frontend/apps/widgets/src/utils/colorUtils.js
/**
 * Convertit une couleur hexadécimale en composantes RGB
 * @param {string} hex - Couleur au format hexadécimal (#RRGGBB)
 * @return {Object} - Composantes {r, g, b}
 */
export const hexToRgb = (hex) => {
  // Retire le # si présent
  const cleanHex = hex.replace('#', '');

  // Convertir en RGB
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  return {r, g, b};
};

/**
 * Convertit des composantes RGB en format hexadécimal
 * @param {number} r - Composante rouge (0-255)
 * @param {number} g - Composante verte (0-255)
 * @param {number} b - Composante bleue (0-255)
 * @return {string} - Couleur au format hexadécimal
 */
export const rgbToHex = (r, g, b) => {
  return '#' +
    Math.max(0, Math.min(255, Math.round(r))).toString(16).padStart(2, '0') +
    Math.max(0, Math.min(255, Math.round(g))).toString(16).padStart(2, '0') +
    Math.max(0, Math.min(255, Math.round(b))).toString(16).padStart(2, '0');
};

/**
 * Assombrit une couleur d'un certain pourcentage
 * @param {string} hex - Couleur au format hexadécimal
 * @param {number} percentage - Pourcentage d'assombrissement (0-100)
 * @return {string} - Couleur assombrie au format hexadécimal
 */
export const darkenColor = (hex, percentage = 15) => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) {
    return hex; // Retourner la valeur d'origine si invalide
  }

  const {r, g, b} = hexToRgb(hex);
  const factor = 1 - (percentage / 100);

  return rgbToHex(
      r * factor,
      g * factor,
      b * factor,
  );
};

/**
 * Éclaircit une couleur d'un certain pourcentage
 * @param {string} hex - Couleur au format hexadécimal
 * @param {number} percentage - Pourcentage d'éclaircissement (0-100)
 * @return {string} - Couleur éclaircie au format hexadécimal
 */
export const lightenColor = (hex, percentage = 15) => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) {
    return hex; // Retourner la valeur d'origine si invalide
  }

  const {r, g, b} = hexToRgb(hex);
  const factor = percentage / 100;

  return rgbToHex(
      r + (255 - r) * factor,
      g + (255 - g) * factor,
      b + (255 - b) * factor,
  );
};

/**
 * Crée un dégradé à partir d'une couleur de base
 * @param {string} primaryColor - Couleur primaire au format hexadécimal
 * @param {Object} options - Options de dégradé
 * @return {string} - Chaîne CSS pour le dégradé
 */
export const createGradientFromColor = (primaryColor, options = {}) => {
  if (!primaryColor || typeof primaryColor !== 'string' || !primaryColor.startsWith('#')) {
    return null; // Retourner null si la couleur est invalide
  }

  const {
    darkenAmount = 15,
    angle = 45,
    startPosition = 30,
    endPosition = 90,
    useDarkerStart = true,
  } = options;

  // Créer la couleur secondaire (plus foncée ou plus claire)
  const secondaryColor = useDarkerStart ?
    darkenColor(primaryColor, darkenAmount) :
    lightenColor(primaryColor, darkenAmount);

  // Déterminer quelle couleur va au début et à la fin
  const startColor = useDarkerStart ? secondaryColor : primaryColor;
  const endColor = useDarkerStart ? primaryColor : secondaryColor;

  // Créer la chaîne CSS du dégradé
  return `linear-gradient(${angle}deg, ${startColor} ${startPosition}%, ${endColor} ${endPosition}%)`;
};
